/**
 * "Soften loud calls" (Settings → Dialer). A limiter on what the agent hears: anything well above
 * normal phone speech — loud voicemail greetings, the beep, carrier tones — comes down ~4–10 dB;
 * normal and quiet talking pass unchanged (Chrome's compressor, measured 2026-10-23: within 0.6 dB).
 *
 * Hooked on the call's `audio` event (TwilioDeviceContext.setupCallListeners), which every call
 * passes through: all dialers, inbound, transfers, both carriers. The processed stream replaces the
 * element's own; speaker pick, volume and sink stay the element's.
 *
 * Off = nothing touched. Can't run (not Chrome/Edge, audio not allowed yet, any error) = the call keeps
 * its own stream: this can never make a call silent.
 *
 * ⚠️ Not the SDK's `audio.addProcessor(p, true)`: its processed stream lives in ONE device-wide slot
 * that any call's teardown destroys — an unanswered inbound call cancelling while the dialer is on
 * a live call would stop the live call's audio.
 * ⚠️ Chrome feeds Web Audio silence from an <audio> playing a WebRTC stream (createMediaElementSource),
 * so this reads the stream itself.
 */

const KEY = "softenLoudCalls";
// Phone speech peaks sit around -10 dBFS; loud greetings, beeps and tones go above it.
const THRESHOLD_DB = -10;
const RATIO = 20;
// The compressor adds automatic makeup gain, (1 / curve(0 dBFS))^0.6 per the Web Audio spec. Undo it
// so audio under the threshold comes out at exactly the level it went in.
const TRIM = 10 ** ((0.6 * THRESHOLD_DB * (1 - 1 / RATIO)) / 20);

type Hooked = { el: HTMLAudioElement; raw: MediaStream | null; out: MediaStream | null; stop: (() => void) | null };
const hooked = new Set<Hooked>();
let ctx: AudioContext | null = null;

export function softenLoudCallsOn(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Chrome/Edge (any Chromium, Android too) only: verified there on the real SDK media path over real
 * WebRTC. Not Safari: without setSinkId the Twilio SDK plays calls through an element it never hands
 * out (its fallback skips the `audio` event). Not iPhone/iPad: call audio stays out of Web Audio there
 * (see setupSpeakerAudioPipeline); every iOS browser is WebKit, none says "Chrome/". Not Firefox: its
 * WebRTC couldn't be tested here, and an unverified path isn't worth a silent call.
 */
export function canSoftenLoudCalls(): boolean {
  return typeof AudioContext !== "undefined" && /Chrome\//.test(navigator.userAgent);
}

/** The Settings switch. Saved on this browser; applies to a live call right away. */
export function setSoftenLoudCalls(on: boolean) {
  try {
    localStorage.setItem(KEY, on ? "1" : "0");
  } catch {
    /* storage blocked: the switch lasts until reload */
  }
  hooked.forEach((h) => (on ? void soften(h) : restore(h)));
}

/** A call's remote <audio>, from its `audio` event. Returns the cleanup for that call's disconnect. */
export function softenCallAudio(el: HTMLAudioElement): () => void {
  const h: Hooked = { el, raw: null, out: null, stop: null };
  hooked.add(h);
  const ready = () => void soften(h);
  // Twilio hands the element over before attaching the stream; Telnyx after.
  if (el.srcObject) ready();
  else el.addEventListener("loadstart", ready, { once: true });
  return () => {
    el.removeEventListener("loadstart", ready);
    hooked.delete(h);
    h.stop?.(); // the call is over: no stream to put back
    h.stop = null;
    if (![...hooked].some((x) => x.stop)) void ctx?.suspend().catch(() => {});
  };
}

async function soften(h: Hooked) {
  if (h.stop || !softenLoudCallsOn() || !canSoftenLoudCalls()) return;
  const raw = h.el.srcObject;
  if (!(raw instanceof MediaStream) || !raw.getAudioTracks().length) return;
  try {
    ctx ??= new AudioContext();
    if (ctx.state !== "running") await Promise.race([ctx.resume(), new Promise((r) => setTimeout(r, 500))]);
    // Audio not allowed yet (no click on this page), or the call moved on while we waited.
    if (ctx.state !== "running" || h.stop || !hooked.has(h) || h.el.srcObject !== raw || !softenLoudCallsOn()) return;
    const source = ctx.createMediaStreamSource(raw);
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = THRESHOLD_DB;
    limiter.knee.value = 0;
    limiter.ratio.value = RATIO;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;
    const trim = ctx.createGain();
    trim.gain.value = TRIM;
    const out = ctx.createMediaStreamDestination();
    source.connect(limiter).connect(trim).connect(out);
    // Chrome only feeds a call's WebRTC audio to Web Audio while some element plays it, and the call's
    // element is about to play ours instead: without this muted one the limiter hears silence.
    const keep = new Audio();
    keep.muted = true;
    keep.srcObject = raw;
    keep.play().catch(() => {});
    h.raw = raw;
    h.out = out.stream;
    h.stop = () => {
      source.disconnect();
      out.stream.getTracks().forEach((t) => t.stop());
      keep.srcObject = null;
    };
    h.el.srcObject = out.stream;
    h.el.play().catch(() => {});
  } catch {
    /* keep the call's own stream */
  }
}

function restore(h: Hooked) {
  if (!h.stop) return;
  h.stop();
  h.stop = null;
  // Only if the element still plays ours: the SDK may have moved on (teardown sets it to null).
  if (h.el.srcObject === h.out) {
    h.el.srcObject = h.raw;
    h.el.play().catch(() => {});
  }
  h.raw = null;
  h.out = null;
}
